import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { maintenanceApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Wrench,
  Plus,
  ArrowLeft,
  Camera,
  Send,
  Loader2,
  Sparkles,
  AlertCircle,
  Clock,
  CheckCircle2,
  ChevronRight,
  Filter,
} from 'lucide-react';

export default function MaintenancePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const photoInputRef = useRef(null);
  const [view, setView] = useState('list'); // 'list' | 'new'
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState('all');
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'medium',
    photos: [],
  });
  const [aiResult, setAiResult] = useState(null);

  const hoaId = user?.hoa_id || null;

  useEffect(() => {
    if (hoaId) {
      fetchTickets();
    } else {
      setLoading(false);
    }
  }, [filter, hoaId]);

  const fetchTickets = async () => {
    if (!hoaId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const status = filter === 'all' ? null : filter;
      const data = await maintenanceApi.list(hoaId, status);
      setTickets(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setTickets([]);
      toast.error(error?.message || 'Could not load tickets — please refresh');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const MAX_FILES = 5;
    const MAX_SIZE_BYTES = 8 * 1024 * 1024;  // 8MB per photo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    const slotsLeft = MAX_FILES - newTicket.photos.length;
    if (slotsLeft <= 0) {
      toast.error(`Maximum ${MAX_FILES} photos`);
      e.target.value = '';
      return;
    }

    const validFiles = files.slice(0, slotsLeft).filter((f) => {
      if (!allowedTypes.includes(f.type) && !f.type.startsWith('image/')) {
        toast.error(`${f.name}: not a supported image type`);
        return false;
      }
      if (f.size > MAX_SIZE_BYTES) {
        toast.error(`${f.name}: too large (max 8MB)`);
        return false;
      }
      return true;
    });

    const dataUrls = await Promise.all(
      validFiles.map(
        (f) => new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(f);
        })
      )
    );

    setNewTicket((nt) => ({ ...nt, photos: [...nt.photos, ...dataUrls] }));
    e.target.value = '';
    if (dataUrls.length) toast.success(`${dataUrls.length} photo${dataUrls.length > 1 ? 's' : ''} attached`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hoaId) {
      toast.error('No building linked — join a building to submit a ticket');
      return;
    }
    if (!newTicket.title || !newTicket.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      // [TICKET-TRACE] temporary diagnostic logging — remove after verification
      const tracePayload = {
        hoa_id: hoaId,
        unit_id: user?.unit_id,
        title: newTicket.title,
        description: newTicket.description,
        priority: newTicket.priority,
        photo_count: (newTicket.photos || []).length,
      };
      console.log('[TICKET-TRACE] submit ->', tracePayload);
      const result = await maintenanceApi.create({
        hoa_id: hoaId,
        unit_id: user?.unit_id,
        title: newTicket.title,
        description: newTicket.description,
        priority: newTicket.priority,
        photos: newTicket.photos,
      });
      console.log('[TICKET-TRACE] API response <-', result);

      setAiResult({
        category: result.ai_category,
        priority: result.ai_priority,
        summary: result.ai_summary,
      });

      toast.success('Maintenance request submitted!');
      setNewTicket({ title: '', description: '', priority: 'medium', photos: [] });
      
      // Show AI result briefly then go back to list
      setTimeout(() => {
        setAiResult(null);
        setView('list');
        fetchTickets();
      }, 3000);
    } catch (error) {
      toast.error(error.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'in_progress': return <AlertCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle2 className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700',
      in_progress: 'bg-sky-100 text-sky-700',
      completed: 'bg-emerald-100 text-emerald-700',
    };
    return colors[status] || colors.pending;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-slate-100 text-slate-600',
      medium: 'bg-amber-100 text-amber-700',
      high: 'bg-orange-100 text-orange-700',
      urgent: 'bg-red-100 text-red-700',
    };
    return colors[priority] || colors.medium;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (view === 'new') {
    return (
      <div className="min-h-screen bg-slate-50 pb-24">
        {/* Header */}
        <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setView('list')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Report Issue</h1>
          </div>
        </header>

        <main className="max-w-md mx-auto px-4 py-6">
          {aiResult ? (
            <Card className="animate-slideUp" data-testid="ai-result-card">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="text-xl font-bold font-manrope text-slate-900 mb-2">Request Submitted!</h2>
                <p className="text-slate-500 mb-4">Our AI has analyzed your issue</p>
                <div className="bg-slate-50 rounded-xl p-4 text-left space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Category</span>
                    <Badge variant="secondary">{aiResult.category}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Priority</span>
                    <Badge className={getPriorityColor(aiResult.priority)}>{aiResult.priority}</Badge>
                  </div>
                  {aiResult.summary && (
                    <div>
                      <span className="text-sm text-slate-500">Summary</span>
                      <p className="text-sm text-slate-700 mt-1">{aiResult.summary}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Issue Title *</label>
                <Input
                  placeholder="e.g., Leaking tap in bathroom"
                  value={newTicket.title}
                  onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                  className="h-12"
                  data-testid="issue-title-input"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Description *</label>
                <Textarea
                  placeholder="Describe the issue in detail..."
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  className="min-h-32 resize-none"
                  data-testid="issue-description-input"
                  required
                />
                <p className="text-xs text-slate-400">Our AI will automatically categorize and prioritize your request</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Priority Level</label>
                <Select 
                  value={newTicket.priority} 
                  onValueChange={(value) => setNewTicket({ ...newTicket, priority: value })}
                >
                  <SelectTrigger className="h-12" data-testid="priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low - Not urgent</SelectItem>
                    <SelectItem value="medium">Medium - Can wait a few days</SelectItem>
                    <SelectItem value="high">High - Needs attention soon</SelectItem>
                    <SelectItem value="urgent">Urgent - Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Photos (Optional)</label>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  capture="environment"
                  className="hidden"
                  onChange={handlePhotoSelect}
                  data-testid="photo-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-24 border-dashed flex flex-col gap-2"
                  onClick={() => photoInputRef.current?.click()}
                  data-testid="add-photos-btn"
                >
                  <Camera className="w-6 h-6 text-slate-400" />
                  <span className="text-sm text-slate-500">Tap to add photos (up to 5)</span>
                </Button>
                {newTicket.photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2" data-testid="photo-previews">
                    {newTicket.photos.map((photo, idx) => (
                      <div key={idx} className="relative group" data-testid={`photo-preview-${idx}`}>
                        <img src={photo} alt={`Attachment ${idx + 1}`} className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                        <button
                          type="button"
                          onClick={() => setNewTicket({ ...newTicket, photos: newTicket.photos.filter((_, i) => i !== idx) })}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-80 group-hover:opacity-100"
                          aria-label="Remove photo"
                          data-testid={`remove-photo-${idx}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-full"
                disabled={submitting}
                data-testid="submit-ticket-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </form>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Maintenance</h1>
          </div>
          <Button 
            onClick={() => setView('new')} 
            className="rounded-full bg-slate-900 hover:bg-slate-800"
            data-testid="new-ticket-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Report
          </Button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-4">
        {!hoaId ? (
          <Card data-testid="no-building-card">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <Wrench className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">No building linked</h3>
              <p className="text-slate-500 text-sm mb-4">
                Your account isn't connected to a building yet. Join a building to log maintenance requests.
              </p>
              <Link to="/find-building">
                <Button className="bg-sky-500 hover:bg-sky-600" data-testid="join-building-btn">Find a building</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
        <>
        {/* Filter */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {['all', 'pending', 'in_progress', 'completed'].map((status) => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              size="sm"
              className={`rounded-full whitespace-nowrap ${filter === status ? 'bg-slate-900' : ''}`}
              onClick={() => setFilter(status)}
              data-testid={`filter-${status}`}
            >
              {status === 'all' ? 'All' : status.replace('_', ' ')}
            </Button>
          ))}
        </div>

        {/* Tickets List */}
        <div className="space-y-3">
          {loading ? (
            <>
              <Skeleton className="h-24 rounded-xl" />
              <Skeleton className="h-24 rounded-xl" />
              <Skeleton className="h-24 rounded-xl" />
            </>
          ) : tickets.length > 0 ? (
            tickets.map((ticket, index) => (
              <Card 
                key={ticket.ticket_id} 
                className="overflow-hidden animate-slideUp cursor-pointer hover:shadow-md transition-shadow"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`ticket-${ticket.ticket_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Wrench className="w-5 h-5 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{ticket.title}</p>
                        <p className="text-sm text-slate-500 line-clamp-1">{ticket.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary" className={`text-xs ${getStatusColor(ticket.status)}`}>
                            {getStatusIcon(ticket.status)}
                            <span className="ml-1">{ticket.status.replace('_', ' ')}</span>
                          </Badge>
                          <Badge variant="secondary" className={`text-xs ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">{formatDate(ticket.created_at)}</p>
                        {ticket.photos && ticket.photos.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`my-ticket-photos-${ticket.ticket_id}`}>
                            {ticket.photos.slice(0, 5).map((p, i) => (
                              <a
                                key={i}
                                href={p}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`my-ticket-photo-${ticket.ticket_id}-${i}`}
                              >
                                <img src={p} alt={`Attachment ${i + 1}`} className="w-12 h-12 object-cover rounded border border-slate-200 hover:opacity-80" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Wrench className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">No tickets found</h3>
                <p className="text-slate-500 text-sm mb-4">
                  {filter === 'all' 
                    ? "You haven't reported any issues yet" 
                    : `No ${filter.replace('_', ' ')} tickets`}
                </p>
                <Button 
                  onClick={() => setView('new')}
                  className="rounded-full bg-slate-900 hover:bg-slate-800"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Report Issue
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
        </>
        )}
      </main>
    </div>
  );
}
